import type { StatePensionAgeConfig } from '@ben/api/gen/codegen.schemas';
import { useStatePensionAgeConfigsList } from '@ben/api/gen/codegen';
import { COUNTRIES } from '@ben/common/countries';
import Checkbox from '@ben/orion/checkbox';
import type { ISelectOption } from '@ben/orion/deprecated-select';
import Select from '@ben/orion/deprecated-select';
import Input from '@ben/orion/input';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { type IModularBenefitDetail } from '@ben/shared/types/columbia';
import { StartsOnEnum } from '@ben/shared/types/modular-benefit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';
import useSetDirtyValue from '../hooks/use-set-dirty-value';
import {
  EnrollmentDateType,
  EnrollmentDateTypeSelector,
} from './date-type-selector';
import { DynamicEnrollmentEndConfig } from './dynamic-enrollment-end-config';
import { FixedEnrollmentConfig } from './fixed-enrollment-config';
import { PostponementConfig } from './postponement-config';
import { ResetEnrollmentsOnEndDate } from './reset-enrollments-on-end-date';

const startDateOverrideOptions: ISelectOption[] = [
  { key: 'CUSTOM_START', label: 'Custom start date' },
  { key: 'EMPLOYMENT_START', label: 'Employment start date' },
];

const endDateOverrideOptions: ISelectOption[] = [
  { key: 'CUSTOM_END', label: 'Custom end date' },
  { key: 'EMPLOYMENT_END', label: 'Employment end date' },
];

const optInTimestampOverrideOptions: ISelectOption[] = [
  { key: 'EMPLOYMENT_START', label: 'Employment start date' },
];

const optOutTimestampOverrideOptions: ISelectOption[] = [
  { key: 'EMPLOYMENT_END', label: 'Employment end date' },
];

const EnrollmentConfig = () => {
  const [startDateType, setStartDateType] = useState<
    EnrollmentDateType | undefined
  >();
  const [endDateType, setEndDateType] = useState<
    EnrollmentDateType | undefined
  >();

  const { control } = useFormContext<IModularBenefitDetail>();
  const setDirtyValue = useSetDirtyValue<IModularBenefitDetail>();
  const enrollmentConfig = useWatch({
    control,
    name: 'enrollmentConfig',
    exact: true,
  });
  const startDateOverride = useWatch({
    control,
    name: 'startDateOverride',
    exact: true,
  });
  const endDateOverride = useWatch({
    control,
    name: 'endDateOverride',
    exact: true,
  });

  const { data: statePensionAgeConfigs } = useStatePensionAgeConfigsList() as {
    data: StatePensionAgeConfig[];
  };

  const statePensionAgeConfigOptions: ISelectOption[] = useMemo(() => {
    return (
      statePensionAgeConfigs?.map((config) => ({
        key: config.id.toString(),
        label:
          COUNTRIES.find((country) => country.alpha_2_code === config.country)
            ?.en_short_name ?? config.country,
      })) || []
    );
  }, [statePensionAgeConfigs]);

  const handleStartDateTypeChange = (type: EnrollmentDateType) => {
    setStartDateType(type);
    switch (type) {
      case EnrollmentDateType.NONE: {
        if (endDateType === EnrollmentDateType.NONE) {
          setDirtyValue('enrollmentConfig', null);
          break;
        }
        setDirtyValue('enrollmentConfig.startsOn', null);
        setDirtyValue('enrollmentConfig.fixedEnrollmentStartDate', null);
        setDirtyValue('enrollmentConfig.enrollAfter', null);
        setDirtyValue('enrollmentConfig.timeUnit', null);
        break;
      }
      case EnrollmentDateType.DYNAMIC: {
        setDirtyValue('enrollmentConfig.fixedEnrollmentStartDate', null);
        if (!enrollmentConfig?.startsOn) {
          setDirtyValue(
            'enrollmentConfig.startsOn',
            StartsOnEnum.ELIGIBILITY_START
          );
        }
        break;
      }
      case EnrollmentDateType.FIXED: {
        setDirtyValue('enrollmentConfig.enrollAfter', null);
        setDirtyValue('enrollmentConfig.timeUnit', null);
        break;
      }
    }
  };

  const handleEndDateTypeChange = (type: EnrollmentDateType) => {
    setEndDateType(type);
    switch (type) {
      case EnrollmentDateType.NONE: {
        if (startDateType === EnrollmentDateType.NONE) {
          setDirtyValue('enrollmentConfig', null);
          break;
        }
        setDirtyValue('enrollmentConfig.fixedEnrollmentEndDate', null);
        setDirtyValue('enrollmentConfig.dynamicEnrollmentEndDate', null);
        setDirtyValue('enrollmentConfig.manualEnrollmentEndDate', false);
        break;
      }
      case EnrollmentDateType.MANUAL: {
        setDirtyValue('enrollmentConfig.manualEnrollmentEndDate', true);
        setDirtyValue('enrollmentConfig.fixedEnrollmentEndDate', null);
        setDirtyValue('enrollmentConfig.dynamicEnrollmentEndDate', null);
        break;
      }
      case EnrollmentDateType.DYNAMIC: {
        setDirtyValue('enrollmentConfig.fixedEnrollmentEndDate', null);
        setDirtyValue('enrollmentConfig.manualEnrollmentEndDate', false);
        break;
      }
      case EnrollmentDateType.FIXED: {
        setDirtyValue('enrollmentConfig.dynamicEnrollmentEndDate', null);
        setDirtyValue('enrollmentConfig.manualEnrollmentEndDate', false);
        break;
      }
    }
  };

  const determineDateType = useCallback(
    (
      typeSetter: (type: EnrollmentDateType) => void,
      fixedField: string,
      dynamicField: string,
      manualField?: string
    ) => {
      if ((enrollmentConfig as Record<string, unknown>)?.[fixedField]) {
        typeSetter(EnrollmentDateType.FIXED);
      } else if (
        (enrollmentConfig as Record<string, unknown>)?.[dynamicField]
      ) {
        typeSetter(EnrollmentDateType.DYNAMIC);
      } else if (
        manualField &&
        (enrollmentConfig as Record<string, unknown>)?.[manualField]
      ) {
        typeSetter(EnrollmentDateType.MANUAL);
      } else {
        typeSetter(EnrollmentDateType.NONE);
      }
    },
    [enrollmentConfig]
  );

  useEffect(() => {
    if (!endDateType) {
      determineDateType(
        setEndDateType,
        'fixedEnrollmentEndDate',
        'dynamicEnrollmentEndDate',
        'manualEnrollmentEndDate'
      );
    }
  }, [enrollmentConfig, endDateType, determineDateType]);

  useEffect(() => {
    if (!startDateType) {
      determineDateType(
        setStartDateType,
        'fixedEnrollmentStartDate',
        'startsOn'
      );
    }
  }, [enrollmentConfig, startDateType, determineDateType]);

  return (
    <Container>
      <HeaderContainer>
        <Typography variant={TypographyVariant.h2}>Enrolment Config</Typography>
      </HeaderContainer>
      <ContentContainer>
        <EnrollmentDateTypeSelector
          label="Enrolment start date type"
          value={startDateType || EnrollmentDateType.NONE}
          exclude={[EnrollmentDateType.MANUAL]}
          onChange={handleStartDateTypeChange}
          dateType="start"
        />
        {startDateType === EnrollmentDateType.FIXED && (
          <FixedEnrollmentConfig type="start" />
        )}
        {startDateType === EnrollmentDateType.DYNAMIC && <PostponementConfig />}
        <EnrollmentDateTypeSelector
          label="Enrolment end date type"
          value={endDateType || EnrollmentDateType.NONE}
          onChange={handleEndDateTypeChange}
          dateType="end"
        />
        {endDateType === EnrollmentDateType.FIXED && (
          <FixedEnrollmentConfig type="end" />
        )}
        {endDateType === EnrollmentDateType.DYNAMIC && (
          <DynamicEnrollmentEndConfig />
        )}
        {endDateType !== undefined &&
          endDateType !== EnrollmentDateType.NONE && (
            <ResetEnrollmentsOnEndDate endDateType={endDateType} />
          )}
        <ContentRow>
          <Controller
            name="startDateOverride"
            control={control}
            render={({ field }) => {
              const { value, onChange, ...rest } = field;
              return (
                <Select
                  {...rest}
                  name="Start date override"
                  options={startDateOverrideOptions}
                  width="100%"
                  optionsTooltipMaxCharCount={40}
                  value={value?.toString()}
                  clearable
                  onChange={onChange}
                  data-testid="start-date-override-select"
                />
              );
            }}
          />
          <Controller
            name="startDateOverrideField"
            control={control}
            render={({ field }) => {
              const { value, ...rest } = field;
              return (
                <Input
                  {...rest}
                  name="Start date override field"
                  hintText="Only available if start date override is set to custom. Please enter the relevant custom field key."
                  value={value ?? ''}
                  width="100%"
                  disabled={startDateOverride !== 'CUSTOM_START'}
                />
              );
            }}
          />
        </ContentRow>
        <ContentRow>
          <Controller
            name="endDateOverride"
            control={control}
            render={({ field }) => {
              const { value, onChange, ...rest } = field;
              return (
                <Select
                  {...rest}
                  name="End date override"
                  options={endDateOverrideOptions}
                  width="100%"
                  optionsTooltipMaxCharCount={40}
                  value={value?.toString()}
                  clearable
                  onChange={onChange}
                  data-testid="end-date-override-select"
                />
              );
            }}
          />
          <Controller
            name="endDateOverrideField"
            control={control}
            render={({ field }) => {
              const { value, ...rest } = field;
              return (
                <Input
                  {...rest}
                  name="End date override field"
                  hintText="Only available if end date override is set to custom. Please enter the relevant custom field key."
                  value={value ?? ''}
                  width="100%"
                  disabled={endDateOverride !== 'CUSTOM_END'}
                />
              );
            }}
          />
        </ContentRow>
        <ContentRow>
          <Controller
            name="optInTimestampOverride"
            control={control}
            render={({ field }) => {
              const { value, onChange, ...rest } = field;
              return (
                <Select
                  {...rest}
                  key={value?.toString()}
                  name="Opt in timestamp override"
                  options={optInTimestampOverrideOptions}
                  width="100%"
                  optionsTooltipMaxCharCount={40}
                  defaultValue={value?.toString()}
                  clearable
                  onChange={onChange}
                  data-testid="opt-in-timestamp-override-select"
                />
              );
            }}
          />
          <Controller
            name="optOutTimestampOverride"
            control={control}
            render={({ field }) => {
              const { value, onChange, ...rest } = field;
              return (
                <Select
                  {...rest}
                  key={value?.toString()}
                  name="Opt out timestamp override"
                  options={optOutTimestampOverrideOptions}
                  width="100%"
                  optionsTooltipMaxCharCount={40}
                  defaultValue={value?.toString()}
                  clearable
                  onChange={onChange}
                  data-testid="opt-out-timestamp-override-select"
                />
              );
            }}
          />
        </ContentRow>
        <ContentRow>
          <Controller
            name="keepEnrolledUntilSpa"
            control={control}
            render={({ field, fieldState: { isDirty } }) => {
              return (
                <Checkbox
                  label="Keep enrolled until state pension age"
                  {...field}
                  value={field.value || false}
                />
              );
            }}
          />
          <Controller
            name="setContactOptInToEmployeeOptIn"
            control={control}
            render={({ field }) => {
              return (
                <Checkbox
                  label="Set contact opt-in to employee opt-in"
                  {...field}
                  value={field.value || false}
                />
              );
            }}
          />
        </ContentRow>

        <Controller
          name="statePensionAgeConfig"
          control={control}
          render={({ field, fieldState }) => {
            const { value, onChange, ...rest } = field;
            return (
              <Select
                {...rest}
                key={value?.toString()}
                name="State pension age config"
                options={statePensionAgeConfigOptions}
                optionsTooltipMaxCharCount={40}
                defaultValue={value?.toString()}
                width="385px"
                onChange={onChange}
                clearable
                errorMessage={fieldCannotBeBlankErrorMessage}
                hasError={!!fieldState.error?.message}
                data-testid="state-pension-config-select"
              />
            );
          }}
        />
      </ContentContainer>
    </Container>
  );
};

export default EnrollmentConfig;

const HeaderContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `};
`;

const ContentContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
    border-radius: 10px;
    background-color: ${theme.palette.primary.bg};
    padding: ${theme.spacing(3)};
    width: 100%;
  `};
`;

const ContentRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `};
`;

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(3)};
    border: 1px solid ${theme.palette.neutral[60]};
    border-radius: 10px;
    gap: ${theme.spacing(3)};
    width: 100%;
  `};
`;
